import {
  RangeCalendar as AriaRangeCalendar,
  RangeCalendarProps as AriaRangeCalendarProps,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  DateValue,
  Text,
} from "react-aria-components";
import { focusRing } from "../../utils";
import { CalendarGridHeader, CalendarHeader } from "../Calendar";

/**
 * RangeCalendar
 *
 * Calendar allowing selection of a date range.
 */
export interface RangeCalendarProps<T extends DateValue> extends Omit<AriaRangeCalendarProps<T>, "visibleDuration"> {
  errorMessage?: string;
}

function getCellClassName({
  selectionState,
  isDisabled,
}: {
  selectionState: "none" | "middle" | "cap";
  isDisabled?: boolean;
}) {
  const baseClasses = `${focusRing} w-full h-full flex items-center justify-center rounded-sm forced-color-adjust-none font-semibold`;

  const selectionStateClasses = {
    none: "text-text-primary group-hover:bg-background-muted group-pressed:bg-background-muted",
    middle: [
      "bg-background-selected group-hover:bg-background-muted forced-colors:group-hover:bg-[Highlight]",
      "group-invalid:group-hover:bg-feedback-error-background forced-colors:group-invalid:group-hover:bg-[Mark]",
      "group-pressed:bg-background-muted forced-colors:group-pressed:bg-[Highlight] forced-colors:text-[HighlightText]",
      "group-invalid:group-pressed:bg-feedback-error-background forced-colors:group-invalid:group-pressed:bg-[Mark]",
    ].join(" "),
    cap: "bg-action-default text-[color:var(--color-action-default-text)] group-invalid:bg-feedback-error-border forced-colors:bg-[Highlight] forced-colors:group-invalid:bg-[Mark] forced-colors:text-[HighlightText]",
  };

  const disabledClasses = isDisabled ? "text-text-disabled forced-colors:text-text-disabled font-light" : "";

  return [baseClasses, selectionStateClasses[selectionState], disabledClasses].filter(Boolean).join(" ");
}

export function RangeCalendar<T extends DateValue>({ errorMessage, ...props }: RangeCalendarProps<T>) {
  return (
    <AriaRangeCalendar {...props}>
      <CalendarHeader />
      <CalendarGrid className="w-full border-separate [border-spacing:0] [&_td]:p-0 [&_td]:!border-0">
        <CalendarGridHeader />
        <CalendarGridBody>
          {(date) => (
            <CalendarCell
              date={date}
              className="selection-start:rounded-s-full selection-end:rounded-e-full [td:last-child_&]:rounded-e-end group h-9 w-9 cursor-default text-sm border-0 outside-month:text-text-disabled invalid:selected:bg-feedback-error-background forced-colors:selected:bg-[Highlight] forced-colors:selected:text-[HighlightText] forced-colors:invalid:selected:bg-[Mark] [td:first-child_&]:rounded-s-full [&]:border-0"
            >
              {({
                formattedDate,
                isSelected,
                isSelectionStart,
                isSelectionEnd,
                isFocusVisible: _isFocusVisible,
                isDisabled,
              }) => (
                <span
                  className={getCellClassName({
                    selectionState:
                      isSelected && (isSelectionStart || isSelectionEnd) ? "cap" : isSelected ? "middle" : "none",
                    isDisabled,
                  })}
                >
                  {formattedDate}
                </span>
              )}
            </CalendarCell>
          )}
        </CalendarGridBody>
      </CalendarGrid>
      {errorMessage && (
        <Text slot="errorMessage" className="text-sm text-feedback-error-text">
          {errorMessage}
        </Text>
      )}
    </AriaRangeCalendar>
  );
}
